import React from "react";
import PropTypes from 'prop-types';

import Tooltip from '@components/common/tooltip/main';
import {
  Collection,
  Brandfolder,
  bulkRefreshSections,
  pluralizeObject
} from "@helpers/show_page_helpers";
import { WorkspaceEvents } from '@components/workspace/WorkspaceEnums';

export default class BulkAction extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      approved: this.props.assetsApproved,
      disableAction: false
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.assetsApproved !== prevProps.assetsApproved) {
      this.initialApprovalStatus();
    }
  }

  initialApprovalStatus() {
    this.setState({ approved: this.props.assetsApproved });
  }

  buildDataBody() {
    const { actionType, selectedAssetKeys } = this.props;

    if (actionType === 'BulkApproval') {
      return { data: { asset_keys: Array.from(selectedAssetKeys) } };
    }

    if (actionType === 'BulkCollectionRemoval') {
      return { data: { asset_keys: Array.from(selectedAssetKeys), collection_keys: [Collection.key] } };
    }

    return null;
  }

  removeAssetsFromCollection() {
    this.setState({ disableAction: true });

    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? bfTranslate('assets') : bfTranslate('asset');
    const remainCopy = assetQtyObject.plural ? bfTranslate('remain') : bfTranslate('remains');

    $.ajax({
      type: "DELETE",
      url: '/api/v4/bulk_actions/assets/destroy_collection_associations?queue_priority=high',
      contentType: "application/json",
      data: JSON.stringify(this.buildDataBody()),
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: () => {
        this.setState({ disableAction: false });
        BF.fx.updateAssetPageCount(assetQtyObject.qty, false);
        this.props.addRemoveSelected([]);
        bulkRefreshSections(this.props.selectedAssetKeys);
        Notify.create({
          title: `${bfTranslate('Removed')} ${assetQtyObject.qty} ${assetCopy} ${bfTranslate('from')} ${Collection.name}.`,
          body: `${bfTranslate('Removed')} ${assetCopy} ${bfTranslate('still')} ${remainCopy} ${bfTranslate('in')} ${Brandfolder.name}.`,
          type: 'success'
        });

        const isWorkspace = BFG.hasFeature('workspace') && BFG.resource.is_workspace;
        if (isWorkspace) {
          // update the workspace dashbar counts and percentages
          const workspaceUpdatedEvent = new CustomEvent(WorkspaceEvents.WorkspaceUpdated, {
            detail: {
              refresh: true
            }
          });
          window.dispatchEvent(workspaceUpdatedEvent);
        }
      },
      error: () => {
        this.setState({ disableAction: false });
        Notify.create({
          title: `${bfTranslate('An error occurred when removing')} ${assetQtyObject.qty} ${assetCopy} ${bfTranslate('from')} ${Collection.name}. ${bfTranslate('Please try again.')}`,
          type: 'error'
        });
      }
    });
  }

  approveAssets() {
    this.setState({ disableAction: true });

    $.ajax({
      type: "POST",
      url: '/api/v4/bulk_actions/assets/approve?queue_priority=high',
      contentType: "application/json",
      data: JSON.stringify(this.buildDataBody()),
      context: this,
      headers: { Authorization: `Bearer ${BF_Token}` },
      success: (response) => {
        this.setState((prevState) => ({ approved: !prevState.approved, disableAction: false }));
        bulkRefreshSections(this.props.selectedAssetKeys);
        const approvalQty = response.required_approvals;
        const approvalCopy = approvalQty === 1 ? bfTranslate('total approval') : bfTranslate('total approvals');
        const bodyText = (approvalQty === 'N/A' || !approvalQty)
          ? ''
          : `${bfTranslate('This asset requires')} ${approvalQty} ${approvalCopy}.`;
        this.makeNotification(bfTranslate('Approved'), bodyText);
      },
      error: () => {
        this.setState({ disableAction: false });
        Notify.create({
          title: bfTranslate('An error occurred when approving assets. Please try again.'),
          type: 'error'
        });
      }
    });
  }

  unapproveAssets() {
    this.setState({ disableAction: true });
    const { activeCdnTracers } = this.props;
    const that = this;

    if (activeCdnTracers && activeCdnTracers > 0) {
      const sweet_alert_options = {
        title: bfTranslate("An asset has active external links."),
        text: bfTranslate("At least one of your selected assets has external links. \n Unapproving will cause broken image links on sites that depend on the availability of these assets. Check the usage tab to see active external links before unapproving.\n\n"),
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Unapprove",
        closeOnConfirm: true,
        customClass: "merging"
      };
      swal(sweet_alert_options, (unapprove_selected) => { // eslint-disable-line no-undef
        if (unapprove_selected) {
          $.ajax({
            type: "POST",
            url: '/api/v4/bulk_actions/assets/unapprove?queue_priority=high',
            contentType: "application/json",
            data: JSON.stringify(that.buildDataBody()),
            context: this,
            headers: { Authorization: `Bearer ${BF_Token}` },
            success: (response) => {
              that.setState((prevState) => ({ approved: !prevState.approved, disableAction: false }));
              bulkRefreshSections(that.props.selectedAssetKeys);
              const approvalQty = response.required_approvals;
              const approvalCopy = approvalQty === 1 ? bfTranslate('approval') : bfTranslate('approvals');
              const bodyText = (approvalQty === 'N/A' || !approvalQty)
                ? ''
                : `${bfTranslate('This asset requires')} ${approvalQty} ${approvalCopy}. ${bfTranslate('All previous approvals are void.')}`;
              that.makeNotification(bfTranslate('Unapproved'), bodyText);
            },
            error: () => {
              that.setState({ disableAction: false });
              Notify.create({
                title: bfTranslate('An error occurred when unapproving assets. Please try again.'),
                type: 'error'
              });
            }
          });
        }
      });
    } else {
      $.ajax({
        type: "POST",
        url: '/api/v4/bulk_actions/assets/unapprove?queue_priority=high',
        contentType: "application/json",
        data: JSON.stringify(this.buildDataBody()),
        context: this,
        headers: { Authorization: `Bearer ${BF_Token}` },
        success: (response) => {
          this.setState((prevState) => ({ approved: !prevState.approved, disableAction: false }));
          bulkRefreshSections(this.props.selectedAssetKeys);
          const approvalQty = response.required_approvals;
          const approvalCopy = approvalQty === 1 ? bfTranslate('approval') : bfTranslate('approvals');
          const bodyText = (approvalQty === 'N/A' || !approvalQty)
            ? ''
            : `${bfTranslate('This asset requires')} ${approvalQty} ${approvalCopy}. ${bfTranslate('All previous approvals are void.')}`;
          this.makeNotification(bfTranslate('Unapproved'), bodyText);
        },
        error: () => {
          this.setState({ disableAction: false });
          Notify.create({
            title: bfTranslate('An error occurred when unapproving assets. Please try again.'),
            type: 'error'
          });
        }
      });
    }
  }

  makeNotification(approvalVerb, bodyText) {
    const assetQtyObject = pluralizeObject(this.props.selectedAssetKeys);
    const assetCopy = assetQtyObject.plural ? bfTranslate('assets') : bfTranslate('asset');

    Notify.create({
      title: `${approvalVerb} ${assetQtyObject.qty} ${assetCopy}!`,
      body: bodyText,
      type: 'success'
    });
  }

  approvalStatus() {
    if (this.state.approved) {
      return { icon: 'bff-unapprove', copy: bfTranslate('Unapprove'), action: () => this.unapproveAssets() };
    }

    return { icon: 'bff-approve', copy: bfTranslate('Approve'), action: () => this.approveAssets() };
  }

  renderBulkApproval(disabledClass) {
    if (!this.props.approver) { return null; }

    return (
      <Tooltip
        className="Approve-tooltip-component"
        offset={`{'top': -20}`}
        tooltipContent={this.approvalStatus().copy}
        tooltipId="BulkApproval-tooltip"
        type="light"
      >
        <div
          className={`bulk-bar-item hover-item ${disabledClass}`}
          onClick={this.approvalStatus().action}
        >
          <span
            aria-hidden="false"
            aria-label={this.approvalStatus().copy}
            className={`icon ${this.approvalStatus().icon}`}
          />
          <h4 className="action-text">{this.approvalStatus().copy}</h4>
        </div>
      </Tooltip>
    );
  }

  renderBulkCollectionRemoval(disabledClass) {
    return (
      <Tooltip
        className="Remove-tooltip-component"
        offset={`{'top': -20}`}
        tooltipContent={bfTranslate('Remove')}
        tooltipId="BulkCollectionRemoval-tooltip"
        type="light"
      >
        <div
          className={`bulk-bar-item hover-item with-text ${disabledClass}`}
          onClick={() => this.removeAssetsFromCollection()}
        >
          <span aria-hidden="true" className="icon bff-trash" />
          <h4 className="action-text">{bfTranslate('Remove')}</h4>
        </div>
      </Tooltip>
    );
  }

  render() {
    const { disableAction } = this.state;
    const { actionType } = this.props;
    const disabledClass = disableAction ? 'disabled-section' : '';

    if (actionType === 'BulkApproval') {
      return this.renderBulkApproval(disabledClass);
    }

    if (actionType === 'BulkCollectionRemoval') {
      return this.renderBulkCollectionRemoval(disabledClass);
    }

    return null;
  }
}

BulkAction.propTypes = {
  actionType: PropTypes.string.isRequired,
  activeCdnTracers: PropTypes.number,
  addRemoveSelected: PropTypes.func.isRequired,
  approver: PropTypes.bool.isRequired,
  assetsApproved: PropTypes.bool.isRequired,
  selectedAssetKeys: PropTypes.instanceOf(Set).isRequired,
};

BulkAction.defaultProps = {
  activeCdnTracers: null,
};
